import { SELECTED_ACCOUNT_CHAIN_ID } from '@hoodmorning/contracts';
import {
  keccak256,
  parseAbi,
  toFunctionSelector,
  type Address,
  type Hex,
  type PublicClient,
} from 'viem';
import { z } from 'zod';
import {
  deriveExternalFixedRouteId,
  deriveSelectedExternalFixedRouteId,
  encodeSelectedExternalFixedRouteIdentity,
  encodeExternalFixedRouteIdentity,
  type ExternalFixedRoute,
  type ExternalFixedRouteIdentityInput,
} from './externalFixedRouteIdentity.js';
import {
  isKnownExperimentalExternalModuleId,
  experimentalExternalFixedRouteOrderAbi,
} from './externalFixedRouteOrderAbi.js';

const UINT256_MAX = (1n << 256n) - 1n;
const addressSchema = z.string().regex(/^0x[0-9a-fA-F]{40}$/)
  .refine(value => !/^0x0{40}$/i.test(value))
  .transform(value => value.toLowerCase() as Address);
const hashSchema = z.string().regex(/^0x[0-9a-fA-F]{64}$/)
  .refine(value => !/^0x0{64}$/i.test(value))
  .transform(value => value.toLowerCase() as Hex);
const pinSchema = z.strictObject({ account: addressSchema, codeHash: hashSchema });
const decimalUint256Schema = z.string().regex(/^(0|[1-9][0-9]*)$/)
  .refine(value => BigInt(value) <= UINT256_MAX);
const sourceSchema = z.strictObject({
  number: z.bigint().min(0n).max(UINT256_MAX),
  hash: hashSchema,
  timestamp: z.bigint().min(0n).max(UINT256_MAX),
});
const envelopeSchema = z.strictObject({
  schemaVersion: z.literal(1),
  profile: z.literal('external-fixed-route-order-profile-v1'),
  chainId: z.literal(4663),
  engine: addressSchema,
  vault: addressSchema,
  keeper: addressSchema,
  owner: addressSchema,
  engineCodeHash: hashSchema,
  reader: pinSchema,
  callBuilder: pinSchema,
  routeId: hashSchema,
  routeIdVersion: z.literal(2).optional(),
  routeIdentityHash: hashSchema,
  route: z.unknown(),
  feeRecipient: addressSchema,
  feeBps: z.number().int().min(0).max(9_999),
  expectedGlobalEpoch: decimalUint256Schema,
  expectedRouteRevision: decimalUint256Schema,
});

export type ExternalOrderObservationProfile<ChainId extends 4663 | 46630 = 4663> = {
  schemaVersion: 1;
  profile: 'external-fixed-route-order-profile-v1';
  chainId: ChainId;
  engine: Address;
  vault: Address;
  keeper: Address;
  owner: Address;
  engineCodeHash: Hex;
  reader: { account: Address; codeHash: Hex };
  callBuilder: { account: Address; codeHash: Hex };
  routeId: Hex;
  routeIdVersion?: 2;
  routeIdentityHash: Hex;
  route: ExternalFixedRoute;
  feeRecipient: Address;
  feeBps: number;
  expectedGlobalEpoch: string;
  expectedRouteRevision: string;
};

export type SelectedExternalOrderObservationProfile = ExternalOrderObservationProfile<typeof SELECTED_ACCOUNT_CHAIN_ID>;

export const externalOrderProfileObservationAbi = [
  ...experimentalExternalFixedRouteOrderAbi,
  ...parseAbi([
    'function authoritySelectors() pure returns(bytes4[6])',
    'function getFallbackSignerData() view returns(address,bool)',
  ]),
] as const;

const readerAbi = parseAbi([
  'struct Pin { address account; bytes32 codeHash; }',
  'struct Asset { address account; bytes32 codeHash; uint8 decimals; }',
  'struct Route { uint8 family; Pin router; Pin permit2; Pin factory; bytes32 poolInitCodeHash; Asset[] tokens; Pin[] pools; uint24[] fees; }',
  'function profileMatches(Route route) view returns(bool)',
]);

const authoritySelectors = [
  'updateFallbackSignerData(address,bool)',
  'installValidation(bytes25,bytes4[],bytes,bytes[])',
  'uninstallValidation(bytes24,bytes,bytes[])',
  'installExecution(address,((bytes4,bool,bool)[],(bytes4,uint32,bool,bool)[],bytes4[]),bytes)',
  'uninstallExecution(address,((bytes4,bool,bool)[],(bytes4,uint32,bool,bool)[],bytes4[]),bytes)',
  'upgradeToAndCall(address,bytes)',
].map(toFunctionSelector);

type ProfileClient = Pick<PublicClient, 'getBytecode' | 'readContract'>;
const same = (left: string, right: string) => left.toLowerCase() === right.toLowerCase();

function orderProfileCodec<const ChainId extends 4663 | 46630>(chainId: ChainId,
  encodeExternalFixedRouteIdentity: (input: ExternalFixedRouteIdentityInput<ChainId>) =>
    { canonicalRoute: ExternalFixedRoute; identityHash: Hex },
  deriveExternalFixedRouteId: (input: ExternalFixedRouteIdentityInput<ChainId>, feeBps?: number) => Hex) {
  const domainEnvelope = envelopeSchema.extend({ chainId: z.literal(chainId) });
  function routeIdFor(profile: Pick<ExternalOrderObservationProfile<ChainId>,
    'chainId' | 'engine' | 'vault' | 'route' | 'routeIdVersion' | 'feeBps'>) {
    return deriveExternalFixedRouteId({ chainId: profile.chainId, engine: profile.engine,
      vault: profile.vault, route: profile.route }, profile.routeIdVersion === 2 ? profile.feeBps : undefined);
  }

  /**
   * Parses one independently trusted engine/profile pin set. Shape and exact
   * domain hashes are checked here; trust in the pin source is not established.
   */
  function readExternalOrderObservationProfile(
    input: ExternalOrderObservationProfile<ChainId>,
  ): ExternalOrderObservationProfile<ChainId> {
    const envelope = domainEnvelope.parse(structuredClone(input));
    const identity = encodeExternalFixedRouteIdentity({
      chainId: envelope.chainId,
      engine: envelope.engine,
      vault: envelope.vault,
      route: envelope.route as ExternalFixedRouteIdentityInput<ChainId>['route'],
    });
    const profile: ExternalOrderObservationProfile<ChainId> = {
      ...envelope,
      route: identity.canonicalRoute,
    };
    if (BigInt(profile.expectedGlobalEpoch) === 0n || BigInt(profile.expectedRouteRevision) === 0n) {
      throw new Error('External order profile inactive epoch or revision');
    }
    if (!same(profile.routeIdentityHash, identity.identityHash)
      || !same(profile.routeId, routeIdFor(profile))) {
      throw new Error('External order profile route domain mismatch');
    }

    const routeRoles = [profile.route.router.account, profile.route.permit2.account,
      profile.route.factory.account, ...profile.route.tokens.map(token => token.account),
      ...profile.route.pools.map(pool => pool.account)];
    // Owner and keeper may legitimately be the same EOA. Only the deployed
    // contract/helper and route identities are structurally distinct roles.
    const distinctRoles = [profile.engine, profile.vault, profile.reader.account,
      profile.callBuilder.account, ...routeRoles];
    if (new Set(distinctRoles).size !== distinctRoles.length) {
      throw new Error('External order profile role collision');
    }
    const forbiddenFees = new Set([
      '0x0000000000000000000000000000000000000001',
      '0x0000000000000000000000000000000000000002',
      profile.engine, profile.vault, profile.reader.account, profile.callBuilder.account,
      ...routeRoles,
    ]);
    if (forbiddenFees.has(profile.feeRecipient)) {
      throw new Error('External order profile fee destination collision');
    }
    return profile;
  }

  async function requireRuntime(
    client: ProfileClient,
    account: Address,
    expectedCodeHash: Hex,
    blockNumber: bigint,
    label: string,
  ) {
    const code = await client.getBytecode({ address: account, blockNumber });
    if (!code || code === '0x' || !same(keccak256(code), expectedCodeHash)) {
      throw new Error(`External order ${label} runtime mismatch`);
    }
  }

  /**
   * Caller-bound, at-block observation of one already selected fixed-route engine
   * profile. The caller must separately establish source canonicality, profile
   * provenance, vault runtime/ownership and installed account topology. Getter
   * failures reject and no observation grants signing or execution authority.
   */
  async function readExternalOrderProfileAtBlock(input: {
    client: ProfileClient;
    profile: ExternalOrderObservationProfile<ChainId>;
    source: { number: bigint; hash: Hex; timestamp: bigint };
  }) {
    // Snapshot all caller-controlled values and bind capabilities before awaiting.
    const profile = readExternalOrderObservationProfile(structuredClone(input.profile));
    const source = sourceSchema.parse(structuredClone(input.source));
    const client: ProfileClient = {
      getBytecode: input.client.getBytecode.bind(input.client) as ProfileClient['getBytecode'],
      readContract: input.client.readContract.bind(input.client) as ProfileClient['readContract'],
    };
    const blockNumber = source.number;

    await requireRuntime(client, profile.engine, profile.engineCodeHash, blockNumber, 'engine');
    const [actualVault, actualKeeper, actualReader, actualCallBuilder, moduleId, selectors,
      globalEpoch, epoch, armed, healthy, nextOrder, routeState, storedProfile] = await Promise.all([
      client.readContract({ address: profile.engine, abi: externalOrderProfileObservationAbi,
        functionName: 'vault', blockNumber }),
      client.readContract({ address: profile.engine, abi: externalOrderProfileObservationAbi,
        functionName: 'keeper', blockNumber }),
      client.readContract({ address: profile.engine, abi: externalOrderProfileObservationAbi,
        functionName: 'reader', blockNumber }),
      client.readContract({ address: profile.engine, abi: externalOrderProfileObservationAbi,
        functionName: 'callBuilder', blockNumber }),
      client.readContract({ address: profile.engine, abi: externalOrderProfileObservationAbi,
        functionName: 'moduleId', blockNumber }),
      client.readContract({ address: profile.engine, abi: externalOrderProfileObservationAbi,
        functionName: 'authoritySelectors', blockNumber }),
      client.readContract({ address: profile.engine, abi: externalOrderProfileObservationAbi,
        functionName: 'globalEpoch', blockNumber }),
      client.readContract({ address: profile.engine, abi: externalOrderProfileObservationAbi,
        functionName: 'epoch', blockNumber }),
      client.readContract({ address: profile.engine, abi: externalOrderProfileObservationAbi,
        functionName: 'armed', blockNumber }),
      client.readContract({ address: profile.engine, abi: externalOrderProfileObservationAbi,
        functionName: 'healthy', blockNumber }),
      client.readContract({ address: profile.engine, abi: externalOrderProfileObservationAbi,
        functionName: 'nextOrder', blockNumber }),
      client.readContract({ address: profile.engine, abi: externalOrderProfileObservationAbi,
        functionName: 'routeStates', args: [profile.routeId], blockNumber }),
      client.readContract({ address: profile.engine, abi: externalOrderProfileObservationAbi,
        functionName: 'routeProfile', args: [profile.routeId, BigInt(profile.expectedRouteRevision)], blockNumber }),
    ]);
    if (!same(actualVault, profile.vault) || !same(actualKeeper, profile.keeper)
      || !same(actualReader, profile.reader.account) || !same(actualCallBuilder, profile.callBuilder.account)) {
      throw new Error('External order engine getter mismatch');
    }
    if (!isKnownExperimentalExternalModuleId(moduleId)
      || selectors.length !== authoritySelectors.length
      || selectors.some((selector, index) => !same(selector, authoritySelectors[index]!))) {
      throw new Error('External order module identity mismatch');
    }

    await Promise.all([
      requireRuntime(client, profile.reader.account, profile.reader.codeHash, blockNumber, 'reader'),
      requireRuntime(client, profile.callBuilder.account, profile.callBuilder.codeHash, blockNumber, 'call builder'),
    ]);
    const [ownerState, admittedByReader] = await Promise.all([
      client.readContract({ address: profile.vault, abi: externalOrderProfileObservationAbi,
        functionName: 'getFallbackSignerData', blockNumber }),
      client.readContract({ address: profile.reader.account, abi: readerAbi,
        functionName: 'profileMatches', args: [profile.route], blockNumber }),
    ]);
    if (!same(ownerState[0], profile.owner) || ownerState[1]) {
      throw new Error('External order owner state mismatch');
    }
    if (!admittedByReader) throw new Error('External order route profile not admitted');

    const expectedEpoch = BigInt(profile.expectedGlobalEpoch);
    const expectedRevision = BigInt(profile.expectedRouteRevision);
    if (globalEpoch !== expectedEpoch || epoch !== expectedEpoch || !armed || !healthy
      || routeState[0] !== expectedRevision || !routeState[1]) {
      throw new Error('External order active context mismatch');
    }
    const storedRouteCommon = {
      router: { ...storedProfile.route.router },
      permit2: { ...storedProfile.route.permit2 },
      factory: { ...storedProfile.route.factory },
      poolInitCodeHash: storedProfile.route.poolInitCodeHash,
      tokens: storedProfile.route.tokens.map(token => ({ ...token })),
      pools: storedProfile.route.pools.map(pool => ({ ...pool })),
    };
    if (storedProfile.route.family !== 2 && storedProfile.route.family !== 3) {
      throw new Error('External order stored profile mismatch');
    }
    const storedRoute = storedProfile.route.family === 2
      ? { family: 2 as const, ...storedRouteCommon, fees: [] }
      : { family: 3 as const, ...storedRouteCommon, fees: [...storedProfile.route.fees] };
    const storedIdentity = encodeExternalFixedRouteIdentity({
      chainId: profile.chainId,
      engine: profile.engine,
      vault: profile.vault,
      route: storedRoute,
    });
    if (!same(storedIdentity.identityHash, profile.routeIdentityHash)
      || !same(storedProfile.routeIdentityHash, profile.routeIdentityHash)
      || !same(storedProfile.feeRecipient, profile.feeRecipient)
      || storedProfile.feeBps !== profile.feeBps
      || JSON.stringify(storedIdentity.canonicalRoute) !== JSON.stringify(profile.route)) {
      throw new Error('External order stored profile mismatch');
    }

    return {
      kind: 'external-fixed-route-order-profile-observation' as const,
      profile,
      source,
      currentOwner: ownerState[0].toLowerCase() as Address,
      ownerDisabled: ownerState[1],
      globalEpoch,
      routeRevision: routeState[0],
      armed,
      healthy,
      nextOrder,
      executionAuthorized: false as const,
      signingAuthorized: false as const,
      profileApproved: false as const,
      canonicalityVerified: false as const,
      provenanceVerified: false as const,
      vaultOwnershipVerified: false as const,
      accountTopologyVerified: false as const,
      finalityProven: false as const,
      verification: 'caller-bound-source-profile-provenance-vault-runtime-ownership-and-account-topology-required' as const,
    };
  }
  return { readExternalOrderObservationProfile, readExternalOrderProfileAtBlock };
}

export const { readExternalOrderObservationProfile, readExternalOrderProfileAtBlock } =
  orderProfileCodec(4663, encodeExternalFixedRouteIdentity, deriveExternalFixedRouteId);
const selectedProfile = orderProfileCodec(SELECTED_ACCOUNT_CHAIN_ID,
  encodeSelectedExternalFixedRouteIdentity, deriveSelectedExternalFixedRouteId);
export const readSelectedExternalOrderObservationProfile = selectedProfile.readExternalOrderObservationProfile;
export const readSelectedExternalOrderProfileAtBlock = selectedProfile.readExternalOrderProfileAtBlock;
export type ExternalOrderProfileObservation<ChainId extends 4663 | 46630 = 4663> = Awaited<
  ReturnType<ReturnType<typeof orderProfileCodec<ChainId>>['readExternalOrderProfileAtBlock']>>;
export type SelectedExternalOrderProfileObservation = ExternalOrderProfileObservation<typeof SELECTED_ACCOUNT_CHAIN_ID>;
